// Manual reservation (walk-in / phone). Reuses the public SeatMap but skips
// /holds — an admin reserves any sellable seat directly.

import { SeatMap } from '@/features/booking/SeatMap/index.tsx';
import { useSeatmap } from '@/features/booking/api/useSeatmap.ts';
import { useSeatmapState } from '@/features/booking/api/useSeatmapState.ts';
import { MVP_EVENT_ID, type SeatDto } from '@okn/shared';
import { Check, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from './auth.ts';
import { Button } from './ui/Button.tsx';
import { Card } from './ui/Card.tsx';
import { Field, Input, Select, Textarea } from './ui/Field.tsx';
import { PageHeader } from './ui/PageHeader.tsx';
import { useToast } from './ui/Toast.tsx';

export function ManualReservationPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const map = useSeatmap(MVP_EVENT_ID);
  const { state } = useSeatmapState(MVP_EVENT_ID, 'admin-manual-session-token');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [source, setSource] = useState<'admin' | 'phone' | 'box_office'>('phone');
  const [sendEmailFlag, setSendEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  const stateCtx = useMemo(
    () => ({
      reservedSeatIds: new Set(state?.reservedSeatIds ?? []),
      soldSeatIds: new Set(state?.soldSeatIds ?? []),
      heldSeatIds: new Set(state?.heldSeatIds ?? []),
    }),
    [state],
  );

  const selectedSeats = useMemo<SeatDto[]>(() => {
    if (map.status !== 'ready') return [];
    return map.data.allSeats.filter((s) => selected.has(s.id));
  }, [map, selected]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await adminApi<{ id: string; referenceCode: string }>('/admin/reservations', {
        method: 'POST',
        body: {
          eventId: MVP_EVENT_ID,
          seatIds: Array.from(selected),
          customer: { fullName, email, phone, note: note.trim() || undefined },
          source,
          sendEmail: sendEmailFlag,
        },
      });
      toast.success(`Rezervasyon oluşturuldu · ${res.referenceCode}`);
      navigate(`/admin/rezervasyonlar/${res.id}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const ready = map.status === 'ready';
  const valid = selectedSeats.length > 0 && fullName && email && phone;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        kicker="Yönetim"
        title="Manuel Rezervasyon"
        description="Koltuk seçin, misafir bilgisini girin. Hold gerekmez — admin doğrudan kaydeder."
      />

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
        <Card className="h-[560px] overflow-hidden p-3">
          {ready ? (
            <SeatMap
              data={map.data}
              selectedIds={selected}
              stateContext={stateCtx}
              onSelect={(seat) => {
                if (!seat.isSellable) return;
                if (stateCtx.reservedSeatIds.has(seat.id) || stateCtx.soldSeatIds.has(seat.id))
                  return;
                setSelected((prev) => {
                  const n = new Set(prev);
                  if (n.has(seat.id)) n.delete(seat.id);
                  else n.add(seat.id);
                  return n;
                });
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-text-faint">
              Koltuk haritası yükleniyor…
            </div>
          )}
        </Card>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card inset className="space-y-4">
            <div>
              <p className="kicker">Seçim</p>
              <p className="mt-1 font-display text-3xl tnum">
                {selectedSeats.length}
                <span className="ml-1.5 text-sm text-text-faint">koltuk</span>
              </p>
              {selectedSeats.length > 0 && (
                <div className="mt-2 flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                  {selectedSeats.map((s) => (
                    <span
                      key={s.id}
                      className="mono rounded-md border border-border bg-surface px-2 py-0.5 text-xs text-text tnum"
                    >
                      {s.label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="hairline" />

            <Field label="Ad Soyad" required>
              {(id) => (
                <Input
                  id={id}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                />
              )}
            </Field>
            <Field label="E-posta" required>
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              )}
            </Field>
            <Field label="Telefon" required>
              {(id) => (
                <Input
                  id={id}
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                />
              )}
            </Field>
            <Field label="Kaynak">
              {(id) => (
                <Select
                  id={id}
                  value={source}
                  onChange={(e) => setSource(e.target.value as typeof source)}
                >
                  <option value="phone">Telefon</option>
                  <option value="box_office">Gişe</option>
                  <option value="admin">Admin</option>
                </Select>
              )}
            </Field>
            <Field label="Not (opsiyonel)">
              {(id) => (
                <Textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              )}
            </Field>

            <label className="flex cursor-pointer items-center gap-2 text-xs text-text-muted">
              <input
                type="checkbox"
                checked={sendEmailFlag}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="accent-accent"
              />
              Misafire e-posta gönder
            </label>

            <Button
              variant="primary"
              size="lg"
              icon={busy ? undefined : <Plus className="h-4 w-4" />}
              loading={busy}
              disabled={!valid}
              onClick={submit}
              className="w-full"
            >
              Rezervasyon oluştur
            </Button>
            {valid && !busy && (
              <p className="flex items-center justify-center gap-1 text-[11px] text-text-faint">
                <Check className="h-3 w-3" /> Bilgiler hazır
              </p>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}
